import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import {
  getBrowserTimezone,
  getFrequencyOptions,
  getGmailStatus,
  setupBusinessGranolaDailyDigestWorkflow,
  setupBusinessNotionDailyDigestWorkflow,
  setupBusinessGmailDailyDigestWorkflow,
  setupBusinessZoomDailyDigestWorkflow,
  setupBusinessGongDailyDigestWorkflow,
  setupBusinessGoogleCalendarDailyDigestWorkflow,
  setupBusinessGoogleMeetDailyDigestWorkflow,
  setupBusinessSlackDailyDigestWorkflow,
  testGranolaApiKey,
  reconnectBusinessWorkflow,
} from "../businessModeService";
import { getLookbackOptions } from "../../core/services/connectorOptions";
import BusinessPageLoader from "./shared/BusinessPageLoader";
import ConnectorNameField from "./shared/ConnectorNameField";
import { useActionItemsSidebarOptional } from "./ActionItemsSidebarContext";
import GmailConnectorSetupPanel from "./shared/GmailConnectorSetupPanel";
import SimpleConnectorSetupPanel from "./shared/SimpleConnectorSetupPanel";
import { clampLookbackDays, DEFAULT_LOOKBACK_DAYS, normalizeLookbackConfig } from "./shared/lookbackPeriod";
import {
  persistExtensionOpenedTab,
  clearBusinessOnboardingConnectSetup,
  persistBusinessOnboardingConnectSetup,
  readBusinessOnboardingConnectSetup,
  tryCloseExtensionOpenedTab,
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  buildActionItemsListPath,
  buildActionItemsSettingsPath,
} from "./actionItemHelpers";
import BusinessAppSettingsChrome from "./BusinessAppSettingsChrome";
import useSettingsNavCollapsed from "./useSettingsNavCollapsed";
import BusinessAppSettingsNav from "./BusinessAppSettingsNav";
import {
  DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE,
  MEETING_RULE_NAME,
} from "./shared/businessFilterPayload";
import {
  buildDigestSetupCompleteSettingsPath,
  buildDigestSetupPayload,
  BUSINESS_DIGEST_DRAFT_STORAGE_KEY,
  BUSINESS_OAUTH_POPUP_RESULT_KEY,
  bounceDesktopOauthCallbackIfNeeded,
  subscribeBusinessOAuthPopupResult,
  canFinishGmailConnectorSetup,
  completeProviderOAuth,
  DIGEST_SETUP_AUTO_RETRY_ATTEMPTS,
  DIGEST_SETUP_AUTO_RETRY_DELAY_MS,
  extractSetupWorkflowId,
  formatDigestSetupError,
  fetchProviderAuthUrl,
  getBusinessDigestDraftStorageKey,
  getRuleByName,
  getWorkflowFilterDefaults,
  getWorkflowSourceConfig,
  isPendingBusinessOAuthCallback,
  isPopupOauthReturn,
  isTransientDigestSetupError,
  parseReconnectCredentialId,
  parseReconnectWorkflowId,
  persistReconnectContext,
  readReconnectContext,
  clearReconnectContext,
  renderConnectorSetupIcon,
  resolveBusinessOauthState,
  shouldAutoFinishConnectorSetup,
  sleep,
  suggestConnectorName,
  toPopupOauthState,
  validateConnectorName,
  upsertNamedRule,
} from "./shared/businessDigestSetup";

const zoomHttpsWorkaroundEnabled =
  String(process.env.REACT_APP_ZOOM_HTTPS_WORKAROUND || "false")
    .trim()
    .toLowerCase() === "true";

const AVAILABLE_WIZARD_STEPS = ["sync"];

const OAUTH_SUCCESS_PARAM = "oauth";
const OAUTH_SUCCESS_VALUE = "success";
const DIGEST_FREQUENCY_VALUES = new Set(["daily", "weekly"]);

const WORKFLOW_SOURCE_ROUTE_PATH = {
  gmail: "/business/workflows/new/email-digest",
  zoom: "/business/workflows/new/zoom-digest",
  google_meet: "/business/workflows/new/google-meet-digest",
  gong: "/business/workflows/new/gong-digest",
  granola: "/business/workflows/new/granola-digest",
  notion: "/business/workflows/new/notion-digest",
  google_calendar: "/business/workflows/new/calendar-digest",
};

const getWorkflowSourceConfigWithRoute = (sourceType) => {
  const base = getWorkflowSourceConfig(sourceType);
  return {
    ...base,
    routePath: WORKFLOW_SOURCE_ROUTE_PATH[sourceType] || WORKFLOW_SOURCE_ROUTE_PATH.gmail,
  };
};

/**
 * Remove persisted digest wizard draft (form fields, step, credential_id).
 *
 * @example
 * clearBusinessDigestWizardDraft();
 * // session key business_digest_wizard_draft_v1 is removed
 */
const clearBusinessDigestWizardDraft = (storageKey = BUSINESS_DIGEST_DRAFT_STORAGE_KEY) => {
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(storageKey);
  }
};

/**
 * Read wizard draft from sessionStorage. Supports legacy shape (form fields only) and
 * wrapped shape { formData, step }.
 *
 * @example
 * parsePersistedWizardDraft('{"frequency":"daily"}');
 * // { formData: { frequency: "daily", ... }, step: "workflow" }
 */
const parsePersistedWizardDraft = (raw) => {
  const parsed = JSON.parse(raw);
  if (
    parsed &&
    typeof parsed === "object" &&
    parsed.formData &&
    typeof parsed.formData === "object"
  ) {
    const stepCandidate = parsed.step;
    const step =
      typeof stepCandidate === "string" && AVAILABLE_WIZARD_STEPS.includes(stepCandidate)
        ? stepCandidate
        : "workflow";
    return { formData: parsed.formData, step };
  }
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    return { formData: parsed, step: "workflow" };
  }
  return null;
};

/**
 * Build initial step + formData from defaults and optional session draft.
 *
 * @example
 * createInitialWizardState(workflowDefaults);
 * // { step: "sync", formData: { frequency: "daily", ... } }
 */
const createInitialWizardState = (
  workflowDefaults,
  workflowType,
  provider,
  storageKey,
  initialStep = "workflow"
) => {
  const defaultForm = {
    workflow_type: workflowType,
    frequency: "daily",
    lookback_days: DEFAULT_LOOKBACK_DAYS,
    credential_id: "",
    credential_provider: "",
    filterOperator: workflowDefaults.filterOperator,
    gmailRules: workflowDefaults.gmailRules,
  };
  if (typeof window === "undefined") {
    return { step: initialStep, formData: defaultForm };
  }
  const raw = window.sessionStorage.getItem(storageKey);
  if (!raw) {
    return { step: initialStep, formData: defaultForm };
  }
  try {
    const bundle = parsePersistedWizardDraft(raw);
    if (!bundle) {
      return { step: initialStep, formData: defaultForm };
    }
    if (
      bundle.formData.workflow_type &&
      bundle.formData.workflow_type !== workflowType
    ) {
      return { step: initialStep, formData: defaultForm };
    }
    const credentialProvider = String(
      bundle.formData.credential_provider || ""
    ).trim();
    const persistedCredentialId =
      credentialProvider === provider ? bundle.formData.credential_id : "";
    const merged = {
      ...defaultForm,
      ...bundle.formData,
      workflow_type: workflowType,
      credential_id: persistedCredentialId,
      credential_provider: persistedCredentialId ? credentialProvider : "",
      filterOperator: bundle.formData.filterOperator || defaultForm.filterOperator,
      gmailRules:
        Array.isArray(bundle.formData.gmailRules) && bundle.formData.gmailRules.length > 0
          ? bundle.formData.gmailRules
          : defaultForm.gmailRules,
    };
    const step =
      typeof bundle.step === "string" && AVAILABLE_WIZARD_STEPS.includes(bundle.step)
        ? bundle.step
        : initialStep;
    const resolvedStep = initialStep === "sync" && step !== "sync" ? "sync" : step;
    return { step: resolvedStep, formData: merged };
  } catch {
    window.sessionStorage.removeItem(storageKey);
    return { step: initialStep, formData: defaultForm };
  }
};

const BusinessDigestWizard = ({ sourceType = "gmail" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const reconnectWorkflowId = useMemo(() => {
    const fromUrl = parseReconnectWorkflowId(location.search);
    if (fromUrl) {
      return fromUrl;
    }
    return String(readReconnectContext()?.workflowId || "").trim();
  }, [location.search]);
  const reconnectCredentialId = useMemo(() => {
    const fromUrl = parseReconnectCredentialId(location.search);
    if (fromUrl) {
      return fromUrl;
    }
    return String(readReconnectContext()?.credentialId || "").trim();
  }, [location.search]);
  const isReconnectMode = Boolean(reconnectWorkflowId);
  const sourceConfig = useMemo(() => getWorkflowSourceConfigWithRoute(sourceType), [sourceType]);
  const isEmailWorkflow = sourceConfig.workflowType === DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE;
  const initialStep = "sync";
  const draftStorageKey = useMemo(
    () => getBusinessDigestDraftStorageKey(sourceConfig.workflowType),
    [sourceConfig.workflowType]
  );
  const workflowDefaults = useMemo(
    () => getWorkflowFilterDefaults(sourceConfig.workflowType),
    [sourceConfig.workflowType]
  );

  const initialWizard = useMemo(
    () =>
      createInitialWizardState(
        workflowDefaults,
        sourceConfig.workflowType,
        sourceConfig.provider,
        draftStorageKey,
        initialStep
      ),
    [workflowDefaults, sourceConfig.workflowType, sourceConfig.provider, draftStorageKey, initialStep]
  );

  const [step, setStep] = useState(initialWizard.step);
  const [loading, setLoading] = useState(true);
  const [optionLoading, setOptionLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sourceConnectLoading, setSourceConnectLoading] = useState(false);
  const [oauthExchanging, setOauthExchanging] = useState(() =>
    isPendingBusinessOAuthCallback(
      typeof window !== "undefined" ? window.location.search : "",
      sourceConfig.oauthState
    )
  );
  const [error, setError] = useState("");
  const [lookbackConfig, setLookbackConfig] = useState({
    quickPresets: [1, 3, 7, 14],
    workflowQuickPresets: [1, 3, 7, 14],
    minDays: 1,
  });
  const [showAdvancedSyncOptions, setShowAdvancedSyncOptions] = useState(false);
  const [formData, setFormData] = useState(initialWizard.formData);
  const [granolaApiKey, setGranolaApiKey] = useState("");
  const [granolaUseApiKey, setGranolaUseApiKey] = useState(false);
  const [granolaApiKeyTesting, setGranolaApiKeyTesting] = useState(false);
  const [granolaApiKeyTestStatus, setGranolaApiKeyTestStatus] = useState("");
  const [granolaApiKeyTestMessage, setGranolaApiKeyTestMessage] = useState("");
  const [isNavCollapsed, toggleNavCollapsed] = useSettingsNavCollapsed();
  // Names of the user's existing connectors, from the shared connector store, so a
  // new one can be required to have a distinct name.
  const sidebar = useActionItemsSidebarOptional();
  const existingConnectorNames = useMemo(
    () =>
      (sidebar?.workflows || [])
        .map((workflow) => String(workflow?.name || "").trim())
        .filter(Boolean),
    [sidebar?.workflows]
  );
  const invalidateConnectors = sidebar?.invalidateConnectors;
  const [connectorNameError, setConnectorNameError] = useState("");
  // Only treat this as onboarding when we explicitly started there, or when an
  // OAuth return needs the session flag (location.state is lost across redirects).
  // Fresh entry from settings must clear any leftover onboarding marker.
  const [isOnboardingConnectSetup] = useState(() => {
    if (location.state?.from === "onboarding") {
      persistBusinessOnboardingConnectSetup();
      return true;
    }
    const params = new URLSearchParams(location.search);
    const isOauthReturn =
      Boolean(params.get("code")) || params.get("oauth") === "success";
    if (isOauthReturn) {
      return readBusinessOnboardingConnectSetup();
    }
    clearBusinessOnboardingConnectSetup();
    return false;
  });
  const oauthPopupRef = useRef(null);
  const pendingAutoCompleteRef = useRef(false);
  const autoFinishStartedRef = useRef(false);

  const resolveExistingCredentialId = useCallback(
    () =>
      String(
        reconnectCredentialId ||
          readReconnectContext()?.credentialId ||
          ""
      ).trim(),
    [reconnectCredentialId]
  );

  useEffect(() => {
    if (!isReconnectMode) {
      return;
    }
    persistReconnectContext({
      workflowId: reconnectWorkflowId,
      credentialId: reconnectCredentialId,
    });
  }, [isReconnectMode, reconnectWorkflowId, reconnectCredentialId]);

  /** Source credential for this workflow only after OAuth in this wizard. */
  const sourceAuthorizedThisWorkflow = Boolean(
    String(formData.credential_id || "").trim() &&
      formData.credential_provider === sourceConfig.provider
  );
  const isGranolaWorkflow = sourceConfig.provider === "granola";
  const isUnifiedSetupWorkflow = true;
  const granolaApiKeyVerified = granolaApiKeyTestStatus === "success";

  useEffect(() => {
    setGranolaApiKeyTestStatus("");
    setGranolaApiKeyTestMessage("");
  }, [granolaApiKey]);

  useEffect(() => {
    if (
      formData.credential_id &&
      formData.credential_provider !== sourceConfig.provider
    ) {
      setFormData((previous) => ({
        ...previous,
        credential_id: "",
        credential_provider: "",
      }));
    }
  }, [formData.credential_id, formData.credential_provider, sourceConfig.provider]);

  useEffect(() => {
    persistExtensionOpenedTab();
  }, [location.search]);

  useEffect(() => {
    window.sessionStorage.setItem(
      draftStorageKey,
      JSON.stringify({ formData, step })
    );
  }, [draftStorageKey, formData, step]);

  useEffect(() => {
    const fetchFormOptions = async () => {
      try {
        setOptionLoading(true);
        const lookbackData = await getLookbackOptions();
        const options = normalizeLookbackConfig(lookbackData || {});
        setLookbackConfig(options);
        setFormData((previous) => ({
          ...previous,
          frequency: "daily",
          lookback_days: clampLookbackDays(previous.lookback_days, {
            minDays: options.minDays,
          }),
        }));
        if (isEmailWorkflow) {
          const frequencyData = await getFrequencyOptions();
          const cadenceOptions = (frequencyData?.frequency_options || []).filter((item) =>
            DIGEST_FREQUENCY_VALUES.has(String(item?.value || "").toLowerCase())
          );
          setFormData((previous) => {
            const validFrequency = cadenceOptions.some(
              (item) => item.value === previous.frequency
            )
              ? previous.frequency
              : "daily";
            return { ...previous, frequency: validFrequency };
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setOptionLoading(false);
      }
    };
    fetchFormOptions();
  }, [isEmailWorkflow]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams(location.search);
        const oauthJustCompleted =
          params.get(OAUTH_SUCCESS_PARAM) === OAUTH_SUCCESS_VALUE;

        if (isEmailWorkflow) {
          await getGmailStatus();
        }

        if (oauthJustCompleted) {
          setStep("sync");
          navigate(sourceConfig.routePath, { replace: true });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [isEmailWorkflow, location.search, location.pathname, navigate, sourceConfig.routePath]);

  // The name to create with once an account is authorised: a name the user typed
  // is kept; the untouched suggestion becomes "<App> - <account email>" when the
  // provider reported the account, so two accounts of one app read differently.
  const existingConnectorNamesRef = useRef(existingConnectorNames);
  existingConnectorNamesRef.current = existingConnectorNames;
  const nameAfterAuthorization = useCallback(
    (form, accountEmail) => {
      if (form.connector_name_edited || isReconnectMode) {
        return form.connector_name || "";
      }
      return suggestConnectorName(
        sourceConfig.providerLabel,
        accountEmail,
        existingConnectorNamesRef.current
      );
    },
    [isReconnectMode, sourceConfig.providerLabel]
  );

  const handleConnectorNameChange = (value) => {
    setConnectorNameError("");
    setFormData((previous) => ({
      ...previous,
      connector_name: value,
      connector_name_edited: true,
    }));
  };

  const connectSource = async () => {
    if (!isReconnectMode) {
      const nameProblem = validateConnectorName(
        formData.connector_name,
        existingConnectorNames
      );
      if (nameProblem) {
        setConnectorNameError(nameProblem);
        return;
      }
    }
    try {
      setError("");
      setSourceConnectLoading(true);
      const { authUrl } = await fetchProviderAuthUrl(
        sourceConfig.provider,
        toPopupOauthState(sourceConfig.oauthState)
      );
      const popup = window.open(authUrl, "_blank");
      oauthPopupRef.current = popup || null;
      // Embedded browsers (e.g. Cursor preview) may open the OAuth tab but return null
      // from window.open because opener access is blocked. OAuth completion is handled
      // via BUSINESS_OAUTH_POPUP_RESULT_KEY storage events in the effect below.
    } catch (err) {
      setError(err.message);
      setSourceConnectLoading(false);
    }
  };

  // While the OAuth popup is open, watch for the result it writes to localStorage,
  // and notice if the user closes the popup without finishing.
  useEffect(() => {
    if (!sourceConnectLoading) {
      return undefined;
    }

    const finishConnect = (result) => {
      setSourceConnectLoading(false);
      oauthPopupRef.current = null;
      if (!result) {
        return;
      }
      if (result.cancelled) {
        return;
      }
      if (result.error) {
        setError(result.error);
        return;
      }
      const { credentialId, accountEmail } = result;
      setFormData((previous) => {
        const next = {
          ...previous,
          credential_id: credentialId || "",
          credential_provider: credentialId ? sourceConfig.provider : "",
          connector_name: nameAfterAuthorization(previous, accountEmail),
        };
        window.sessionStorage.setItem(
          draftStorageKey,
          JSON.stringify({ formData: next, step: "sync" })
        );
        return next;
      });
      pendingAutoCompleteRef.current = true;
    };

    const unsubscribeOauthResult = subscribeBusinessOAuthPopupResult(
      sourceConfig.provider,
      finishConnect
    );
    const readPopupResult = () => {
      const raw = window.localStorage.getItem(BUSINESS_OAUTH_POPUP_RESULT_KEY);
      if (!raw) {
        return null;
      }
      try {
        const result = JSON.parse(raw);
        if (result.provider !== sourceConfig.provider) {
          return null;
        }
        window.localStorage.removeItem(BUSINESS_OAUTH_POPUP_RESULT_KEY);
        return result;
      } catch {
        return null;
      }
    };

    // Popups here always navigate through a third-party OAuth provider (Google/Zoom),
    // which sets its own Cross-Origin-Opener-Policy header and permanently severs this
    // tab's reference to the popup. Reading popup.closed after that point is blocked by
    // COOP and never resolves, so polling it would spin (and console-spam) forever.
    // The storage event above is the reliable completion signal; this timeout is only
    // a last-resort UX fallback in case the popup was closed/blocked before finishing.
    const giveUpTimeout = setTimeout(() => {
      const result = readPopupResult();
      finishConnect(result);
      if (!result) {
        setError((current) =>
          current ||
          "Didn't hear back from the popup. If you finished connecting, refresh this page — otherwise try connecting again."
        );
      }
    }, 120000);

    return () => {
      unsubscribeOauthResult();
      clearTimeout(giveUpTimeout);
    };
  }, [sourceConnectLoading, draftStorageKey, isEmailWorkflow, sourceConfig.provider, nameAfterAuthorization]);

  useEffect(() => {
    const completeOauth = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      if (bounceDesktopOauthCallbackIfNeeded()) {
        return;
      }
      const oauthBaseState = resolveBusinessOauthState(state);
      const isPopupReturn = isPopupOauthReturn(state, sourceConfig.oauthState);
      const host = String(window.location.hostname || "").trim().toLowerCase();
      const isLocalHost = host === "localhost" || host === "127.0.0.1";
      const alreadyBounced = params.get("oauth_host_bounce") === "1";
      if (!code || (oauthBaseState !== sourceConfig.oauthState && !isPopupReturn)) {
        return;
      }

      // Popup OAuth result handoff relies on localStorage in both windows.
      // If the callback lands on ngrok, bounce back to localhost first so the
      // opener can receive BUSINESS_OAUTH_POPUP_RESULT_KEY updates.
      if (
        zoomHttpsWorkaroundEnabled &&
        isPopupReturn &&
        !isLocalHost &&
        !alreadyBounced
      ) {
        params.set("oauth_host_bounce", "1");
        window.location.replace(
          `http://localhost:3000${window.location.pathname}?${params.toString()}`
        );
        return;
      }

      // isPopupReturn is derived from the OAuth `state` string, so it survives even
      // when the provider's own COOP header has nulled out window.opener on this tab
      // (canHandoffOAuthToOpener used to gate on that and silently failed as a result).
      const handoffToOpener = isPopupReturn;
      setOauthExchanging(true);
      setSourceConnectLoading(true);
      try {
        const { credentialId, message, meetApiReady, accountEmail } = await completeProviderOAuth(
          sourceConfig.provider,
          code,
          {
            purpose: sourceConfig.provider === "gmail" ? "workflow" : undefined,
            state,
            existingCredentialId: resolveExistingCredentialId(),
          }
        );
        // Google Meet's OAuth callback probes conferenceRecords.list and reports
        // meetApiReady:false when the account/API can never actually sync (e.g. a
        // Workspace edition without Meet recording/transcripts). Treat that as a
        // hard failure instead of a successful connection.
        const apiUnavailable =
          sourceConfig.provider === "google_meet" && meetApiReady === false;
        if (handoffToOpener) {
          window.localStorage.setItem(
            BUSINESS_OAUTH_POPUP_RESULT_KEY,
            JSON.stringify(
              apiUnavailable
                ? { provider: sourceConfig.provider, error: message }
                : { provider: sourceConfig.provider, credentialId, accountEmail }
            )
          );
          window.history.replaceState({}, "", window.location.pathname);
          setOauthExchanging(false);
          setSourceConnectLoading(false);
          window.close();
          // Some browsers/webviews block script-closing tabs after third-party OAuth.
          // If close fails, leave a stable "done" state instead of appearing to restart auth.
          if (!window.closed) {
            window.document.title = "Authentication complete";
            setError(
              "Authentication complete in this tab. You can close this tab and continue in the original window."
            );
          }
          return;
        }
        if (apiUnavailable) {
          setError(message || "Google Meet API isn't available for this account.");
          setOauthExchanging(false);
          setSourceConnectLoading(false);
          return;
        }
        setFormData((previous) => {
          const next = {
            ...previous,
            credential_id: credentialId || "",
            credential_provider: credentialId ? sourceConfig.provider : "",
            connector_name: nameAfterAuthorization(previous, accountEmail),
          };
          if (typeof window !== "undefined") {
            window.sessionStorage.setItem(
              draftStorageKey,
              JSON.stringify({ formData: next, step: "sync" })
            );
          }
          return next;
        });
        navigate(
          `${sourceConfig.routePath}?oauth=success${
            isReconnectMode
              ? `&reconnect=${encodeURIComponent(reconnectWorkflowId)}${
                  reconnectCredentialId
                    ? `&credential_id=${encodeURIComponent(reconnectCredentialId)}`
                    : ""
                }`
              : ""
          }`,
          {
            replace: true,
          }
        );
        pendingAutoCompleteRef.current = true;
      } catch (err) {
        if (handoffToOpener) {
          window.localStorage.setItem(
            BUSINESS_OAUTH_POPUP_RESULT_KEY,
            JSON.stringify({ provider: sourceConfig.provider, error: err.message })
          );
          window.history.replaceState({}, "", window.location.pathname);
          setOauthExchanging(false);
          setSourceConnectLoading(false);
          window.close();
          if (!window.closed) {
            setError(
              err.message ||
                "OAuth callback completed in this tab. You can close this tab and return to the original window."
            );
          }
          return;
        }
        setError(err.message);
      } finally {
        if (!handoffToOpener) {
          setOauthExchanging(false);
          setSourceConnectLoading(false);
        }
      }
    };
    completeOauth();
  }, [
    draftStorageKey,
    isEmailWorkflow,
    isGranolaWorkflow,
    navigate,
    sourceConfig.oauthState,
    sourceConfig.provider,
    sourceConfig.providerLabel,
    sourceConfig.routePath,
    resolveExistingCredentialId,
    isReconnectMode,
    reconnectWorkflowId,
    reconnectCredentialId,
    nameAfterAuthorization,
  ]);

  const handleTestGranolaApiKey = async () => {
    const trimmedKey = granolaApiKey.trim();
    if (!trimmedKey) {
      setGranolaApiKeyTestStatus("failed");
      setGranolaApiKeyTestMessage("Paste your Granola API key first.");
      return;
    }

    setGranolaApiKeyTesting(true);
    setGranolaApiKeyTestStatus("");
    setGranolaApiKeyTestMessage("");
    try {
      const result = await testGranolaApiKey(trimmedKey);
      setGranolaApiKeyTestStatus("success");
      setGranolaApiKeyTestMessage(result.message || "Granola API key is valid.");
    } catch (err) {
      setGranolaApiKeyTestStatus("failed");
      setGranolaApiKeyTestMessage(err.message || "Invalid Granola API key.");
    } finally {
      setGranolaApiKeyTesting(false);
    }
  };

  const submitSetup = useCallback(async () => {
    // Already validated when connecting; only the API-key path reaches here unchecked.
    const nameProblem = isReconnectMode || formData.credential_id
      ? ""
      : validateConnectorName(formData.connector_name, existingConnectorNamesRef.current);
    if (nameProblem) {
      setConnectorNameError(nameProblem);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = buildDigestSetupPayload({
        sourceConfig,
        formData: { ...formData, frequency: "daily" },
        scheduleTimezone: getBrowserTimezone(),
        granolaApiKey,
        granolaUseApiKey,
        connectorName: isReconnectMode ? "" : formData.connector_name,
      });
      const isCalendarWorkflow = sourceConfig.provider === "google_calendar";
      const isGongWorkflow = sourceConfig.provider === "gong";
      const isGoogleMeetWorkflow = sourceConfig.provider === "google_meet";
      const isSlackWorkflow = sourceConfig.provider === "slack";
      const isNotionWorkflow = sourceConfig.provider === "notion";
      const setupWorkflow = isEmailWorkflow
        ? setupBusinessGmailDailyDigestWorkflow
        : isGranolaWorkflow
          ? setupBusinessGranolaDailyDigestWorkflow
          : isCalendarWorkflow
            ? setupBusinessGoogleCalendarDailyDigestWorkflow
            : isGongWorkflow
              ? setupBusinessGongDailyDigestWorkflow
              : isGoogleMeetWorkflow
                ? setupBusinessGoogleMeetDailyDigestWorkflow
                : isSlackWorkflow
                  ? setupBusinessSlackDailyDigestWorkflow
                  : isNotionWorkflow
                    ? setupBusinessNotionDailyDigestWorkflow
                    : setupBusinessZoomDailyDigestWorkflow;

      let status = null;
      let lastError = null;
      for (let attempt = 1; attempt <= DIGEST_SETUP_AUTO_RETRY_ATTEMPTS; attempt += 1) {
        try {
          status = await setupWorkflow(payload);
          lastError = null;
          break;
        } catch (err) {
          lastError = err;
          const shouldRetry =
            isTransientDigestSetupError(err?.message) &&
            attempt < DIGEST_SETUP_AUTO_RETRY_ATTEMPTS;
          if (!shouldRetry) {
            throw err;
          }
          await sleep(DIGEST_SETUP_AUTO_RETRY_DELAY_MS);
        }
      }
      if (!status) {
        throw lastError || new Error("Setup failed. Please try again.");
      }

      clearBusinessDigestWizardDraft(draftStorageKey);
      invalidateConnectors?.();
      const workflowId = extractSetupWorkflowId(status);
      if (tryCloseExtensionOpenedTab(workflowId)) {
        return;
      }
      // Stay on onboarding so "See my action items" remains the intentional first pull entry.
      if (isOnboardingConnectSetup) {
        clearBusinessOnboardingConnectSetup();
        navigate("/onboarding");
        return;
      }
      clearBusinessOnboardingConnectSetup();
      navigate(
        buildDigestSetupCompleteSettingsPath(workflowId, sourceConfig.workflowType)
      );
    } catch (err) {
      setError(formatDigestSetupError(err.message));
    } finally {
      setSubmitting(false);
    }
  }, [
    draftStorageKey,
    formData,
    granolaApiKey,
    granolaUseApiKey,
    isEmailWorkflow,
    isGranolaWorkflow,
    isOnboardingConnectSetup,
    isReconnectMode,
    invalidateConnectors,
    navigate,
    sourceConfig,
  ]);

  const submitReconnect = useCallback(async () => {
    setSubmitting(true);
    setError("");
    try {
      const credentialId = String(formData.credential_id || "").trim();
      if (!credentialId) {
        throw new Error("Authorization incomplete. Please try reconnecting again.");
      }
      await reconnectBusinessWorkflow(reconnectWorkflowId, {
        credential_id: credentialId,
      });
      clearReconnectContext();
      clearBusinessDigestWizardDraft(draftStorageKey);
      if (tryCloseExtensionOpenedTab(reconnectWorkflowId)) {
        return;
      }
      navigate(
        buildDigestSetupCompleteSettingsPath(
          reconnectWorkflowId,
          sourceConfig.workflowType
        )
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [
    draftStorageKey,
    formData.credential_id,
    navigate,
    reconnectWorkflowId,
    sourceConfig.workflowType,
  ]);

  const submitFinish = isReconnectMode ? submitReconnect : submitSetup;

  const meetingRule = getRuleByName(formData.gmailRules, MEETING_RULE_NAME);
  const gmailCanFinish = canFinishGmailConnectorSetup(
    meetingRule?.senderPatterns || "",
    meetingRule?.subjectKeywords || ""
  );
  const simpleCanFinish = isGranolaWorkflow
    ? granolaUseApiKey
      ? granolaApiKeyVerified
      : sourceAuthorizedThisWorkflow
    : sourceAuthorizedThisWorkflow;
  const setupCanFinish = isEmailWorkflow ? gmailCanFinish : simpleCanFinish;
  const setupShouldAutoFinish = shouldAutoFinishConnectorSetup({
    sourceAuthorized: sourceAuthorizedThisWorkflow,
    isGranolaWorkflow,
    granolaUseApiKey,
    canFinish: setupCanFinish,
    setupFailed: Boolean(error),
  });

  useEffect(() => {
    if (!setupShouldAutoFinish || submitting || autoFinishStartedRef.current) {
      return;
    }
    autoFinishStartedRef.current = true;
    pendingAutoCompleteRef.current = true;
  }, [setupShouldAutoFinish, submitting]);

  useEffect(() => {
    if (!pendingAutoCompleteRef.current) {
      return;
    }
    if (!isUnifiedSetupWorkflow || !setupShouldAutoFinish) {
      if (!setupShouldAutoFinish) {
        pendingAutoCompleteRef.current = false;
      }
      return;
    }
    pendingAutoCompleteRef.current = false;
    void submitFinish();
  }, [
    isUnifiedSetupWorkflow,
    setupShouldAutoFinish,
    submitFinish,
  ]);

  const updateMeetingRule = (updates) => {
    setFormData((previous) => {
      const currentMeetingRule = getRuleByName(previous.gmailRules, MEETING_RULE_NAME);
      return {
        ...previous,
        filterOperator: "OR",
        gmailRules: upsertNamedRule(previous.gmailRules, MEETING_RULE_NAME, {
          enabled: true,
          senderPatterns: currentMeetingRule?.senderPatterns || "",
          subjectKeywords: currentMeetingRule?.subjectKeywords || "",
          ...updates,
        }),
      };
    });
  };

  // Prefill (and keep unique) the suggested name until the user types their own.
  useEffect(() => {
    if (isReconnectMode || formData.connector_name_edited || formData.credential_id) {
      return;
    }
    const suggested = suggestConnectorName(
      sourceConfig.providerLabel,
      "",
      existingConnectorNames
    );
    if (formData.connector_name !== suggested) {
      setFormData((previous) => ({ ...previous, connector_name: suggested }));
    }
  }, [
    isReconnectMode,
    formData.connector_name,
    formData.connector_name_edited,
    formData.credential_id,
    sourceConfig.providerLabel,
    existingConnectorNames,
  ]);

  const connectLoading = sourceConnectLoading || oauthExchanging;

  if (loading) {
    return <BusinessPageLoader text="Loading your workflow status…" />;
  }
  const wizardTitle = isReconnectMode
    ? `Reconnect ${sourceConfig.providerLabel}`
    : sourceConfig.title;

  const exitWizardToWorkflows = () => {
    clearBusinessDigestWizardDraft(draftStorageKey);
    if (tryCloseExtensionOpenedTab()) {
      return;
    }
    const returnPath = isOnboardingConnectSetup
      ? "/onboarding"
      : "/business/settings?settingsTab=connect-apps";
    // Drop the session marker whenever leaving setup so a later settings connect
    // cannot inherit an abandoned onboarding attempt.
    clearBusinessOnboardingConnectSetup();
    navigate(returnPath);
  };

  const exitWizardToTasks = () => {
    clearBusinessDigestWizardDraft(draftStorageKey);
    clearBusinessOnboardingConnectSetup();
    if (tryCloseExtensionOpenedTab()) {
      return;
    }
    navigate(buildActionItemsListPath({}));
  };

  const handleSettingsTabChange = (tab) => {
    clearBusinessDigestWizardDraft(draftStorageKey);
    clearBusinessOnboardingConnectSetup();
    navigate(buildActionItemsSettingsPath({ tab }));
  };

  const setupPanels = (
    <>
      {error ? (
        <div
          className={
            isTransientDigestSetupError(error)
              ? "business-setup-retry-hint"
              : "error-message"
          }
        >
          {error}
        </div>
      ) : null}

      {step === "sync" && !isReconnectMode && !sourceAuthorizedThisWorkflow ? (
        <ConnectorNameField
          value={formData.connector_name || ""}
          onChange={handleConnectorNameChange}
          error={connectorNameError}
          disabled={connectLoading || submitting}
        />
      ) : null}

      {step === "sync" && isEmailWorkflow ? (
        <GmailConnectorSetupPanel
          senderPatterns={meetingRule?.senderPatterns || ""}
          subjectKeywords={meetingRule?.subjectKeywords || ""}
          onSenderPatternsChange={(value) => updateMeetingRule({ senderPatterns: value })}
          onSubjectKeywordsChange={(value) => updateMeetingRule({ subjectKeywords: value })}
          lookbackDays={formData.lookback_days}
          onLookbackDaysChange={(days) =>
            setFormData((previous) => ({
              ...previous,
              lookback_days: days,
            }))
          }
          lookbackConfig={lookbackConfig}
          lookbackLoading={optionLoading}
          showAdvancedOptions={showAdvancedSyncOptions}
          onShowAdvancedOptionsChange={setShowAdvancedSyncOptions}
          sourceAuthorized={sourceAuthorizedThisWorkflow}
          connectLoading={connectLoading}
          submitting={submitting}
          onConnect={connectSource}
          onFinish={submitFinish}
          setupFailed={Boolean(error)}
          onboardingLayout={isOnboardingConnectSetup}
        />
      ) : null}

      {step === "sync" && !isEmailWorkflow ? (
        <SimpleConnectorSetupPanel
          sourceType={sourceType}
          title={wizardTitle}
          privacySummary={
            isReconnectMode
              ? `Sign in again to restore ${sourceConfig.providerLabel} access for this connector.`
              : sourceConfig.subtitle
          }
          lookbackDays={formData.lookback_days}
          onLookbackDaysChange={(days) =>
            setFormData((previous) => ({
              ...previous,
              lookback_days: days,
            }))
          }
          lookbackConfig={lookbackConfig}
          lookbackLoading={optionLoading}
          showAdvancedOptions={showAdvancedSyncOptions}
          onShowAdvancedOptionsChange={setShowAdvancedSyncOptions}
          isGranolaWorkflow={isGranolaWorkflow}
          granolaUseApiKey={granolaUseApiKey}
          onGranolaUseApiKeyChange={setGranolaUseApiKey}
          granolaApiKey={granolaApiKey}
          onGranolaApiKeyChange={setGranolaApiKey}
          onTestGranolaApiKey={handleTestGranolaApiKey}
          granolaApiKeyTesting={granolaApiKeyTesting}
          granolaApiKeyVerified={granolaApiKeyVerified}
          granolaApiKeyTestMessage={
            granolaApiKeyTestStatus === "failed" ? granolaApiKeyTestMessage : ""
          }
          sourceAuthorized={sourceAuthorizedThisWorkflow}
          connectLoading={connectLoading}
          submitting={submitting}
          onConnect={connectSource}
          onFinish={submitFinish}
          setupFailed={Boolean(error)}
          onboardingLayout={isOnboardingConnectSetup}
        />
      ) : null}
    </>
  );

  if (!isOnboardingConnectSetup) {
    return (
      <div className="business-page-content">
        <div className="business-wizard-wrap business-wizard-wrap--app-settings business-wizard-wrap--connector-setup">
          <div className="business-app-settings-shell">
            <BusinessAppSettingsChrome
              isNavCollapsed={isNavCollapsed}
              onToggleNav={toggleNavCollapsed}
              onBackHome={exitWizardToTasks}
            />
            <div
              className={`business-app-settings-layout${
                isNavCollapsed ? " business-app-settings-layout--nav-collapsed" : ""
              }`}
            >
              <BusinessAppSettingsNav
                activeTab={ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS}
                onTabChange={handleSettingsTabChange}
                isCollapsed={isNavCollapsed}
              />
              <div className="business-app-settings-main">
                <div className="business-settings-header business-app-settings-topbar">
                  <div className="business-wizard-header">
                    <button
                      type="button"
                      className="business-wizard-back-btn"
                      onClick={exitWizardToWorkflows}
                      aria-label="Back to connectors"
                    >
                      <IoArrowBack aria-hidden="true" />
                    </button>
                    <div className="business-wizard-header-content">
                      <h2 className="business-wizard-title">Connectors</h2>
                    </div>
                  </div>
                </div>
              <div className="business-app-settings">
                <div className="business-app-settings__body">
                  <div className="business-app-settings__panel business-connector-setup">
                    <div className="ds-detail">
                      <div className="ds-detail__header">
                        {renderConnectorSetupIcon(sourceType, {
                          placement: "header",
                          className: "ds-detail__app-icon",
                        })}
                        <div className="ds-detail__header-text">
                          <h3 className="ds-detail__name">{wizardTitle}</h3>
                        </div>
                      </div>
                      <div className="ds-detail__body">
                        <div className="ds-detail__settings">{setupPanels}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  }

  return (
    <div className="business-wizard-wrap business-wizard-wrap--simple-connect business-wizard-wrap--onboarding-setup">
      <div className="business-settings-header business-app-settings-topbar">
        <div className="business-wizard-header business-wizard-header--with-icon">
          <button
            className="business-wizard-back-btn"
            onClick={exitWizardToWorkflows}
            type="button"
            aria-label="Go back to workflows"
          >
            <IoArrowBack />
          </button>
          <div className="business-wizard-header-center">
            {renderConnectorSetupIcon(sourceType, { placement: "header" })}
            <div className="business-wizard-header-content">
              <h2 className="business-wizard-title">{wizardTitle}</h2>
            </div>
          </div>
        </div>
      </div>
      {setupPanels}
    </div>
  );
};

export default BusinessDigestWizard;
